import {
  Button,
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  Box,
  Text,
  VStack,
  Radio,
  Checkbox,
  RadioGroup,
  CheckboxGroup,
  Input,
  HStack,
} from "@chakra-ui/react";
import {
  dateTodayApiFormat,
  firstDateOfMonthApiFormat,
} from "../../../utils/dateResolvers";
import GaraazSearch from "../../../components/GaraazSearch";
import endpoints from "../../../utils/endpoints";
import TextLabels from "../../PlaceOrderTest/enums";
import { getAuthAxios } from "../../../config/axios";
import globalConstant from "../../../utils/constants";
import useFiltersStore from "../store/useFiltersStore";
import { useOrdersStore } from "../store/useOrdersStore";
import { useEffect, useState, useMemo, useCallback } from "react";
import GaraazCheckBoxTitle from "./GaraazCheckBox";
import { CustomerType } from "../enums";
import {
  useGetAllOrderHandlers,
  useGetDistinctCustomerStates,
} from "../queryHooks";
import { MdKeyboardReturn } from "react-icons/md";
import { GaraazDateRange } from "@svan-garaaz/reactsharedlib";
import { StateSortConfig, SideFilterTabProps } from "./types";
import { OrderStatus } from "../enums";

const sortStatesBySelection = ({
  selectedStates,
  allStates,
}: StateSortConfig): string[] => {
  if (!Array.isArray(allStates)) return [];

  return [...allStates].sort((a: string, b: string) => {
    const aSelected = selectedStates.includes(a);
    const bSelected = selectedStates.includes(b);
    if (aSelected && !bSelected) return -1;
    if (!aSelected && bSelected) return 1;
    return 0;
  });
};

const SideFilterTab = (props: SideFilterTabProps) => {
  const {
    isOpen,
    onClose,
    setOrderStartIndex,
    setIsFiltersChanged,
    refetchAllOrders,
  } = props;

  const authJSON = localStorage.getItem(globalConstant.AUTH_TOKEN) ?? "";
  const authAxios = getAuthAxios(process.env.REACT_APP_BASE_URL, authJSON);
  const { data: allOrderHandlers } = useGetAllOrderHandlers();
  const { data: states, isLoading: isLoadingStates } =
    useGetDistinctCustomerStates();

  const setFilterObject = useFiltersStore((state) => state.setFilterObject);
  const setQueryOffset = useOrdersStore((state) => state.setQueryOffset);
  const filterObject = useFiltersStore((state) => state.filterObject);

  const orderStatuses = [
    "NEW",
    "ACK",
    "CUSTOMER_HOLD",
    "ALLOCATED",
    "CBO",
    "CANCELLED",
    "ENQUIRY_CLOSED",
    "DELIVERED",
    "READY_TO_DISPATCH",
    "BLOCKED",
    "RETURN_REQUESTED",
    "CUSTOMER_RETURNED",
    "CUSTOMER_RETURN_REJECTED",
    "SUPPLIER_RETURNED",
    "SUPPLIER_RETURN_REJECTED",

    "ACCEPTED",
    "REJECTED",
  ];

  const supplierOrderStatuses = [
    "NEW",
    "ACK",
    "CBO",
    "CANCELLED",
    "READY",
    "IN_REVIEW",
    "REVOKED",
    "NONE",
    "RETURN_REQUESTED",
    "ACCEPTED",
    "REJECTED",
  ];

  const orderTypes = ["REGULAR", "RETURN", "SUBSCRIPTION"];
  const transactionTypes = ["Facilitation", "Trading", "NONE"];
  const invoiceStatus = ["PAID", "RECEIVED", "UNPAID"];
  const deliveryModes = ["DOOR_DELIVERY", "SELF_PICKUP"];
  const pendingOrderItems = ["CBO", "SUPPLIER_CANCELLED", "ACTIVE"];
  const [localFilterObject, setLocalFilterObject] = useState<{
    limit: number;
    orderStatus: string;
    supplierOrderStatus: string;
    customerType: string;
    orderHandler: string;
    filterOrderNo: string;
    filterOrderNoCheckBoxValue: boolean;
    customerId: string;
    customerName: string;
    orderType: string;
    transactionType: string;
    invoiceStatus: string;
    isSupplierInvoicePresent: boolean | undefined;
    startDate: string;
    endDate: string;
    states: string;
    deliveryMode: string;
    pendingOrderItems: string;
    voidedStatus: string;
  }>({
    ...filterObject,
    startDate: filterObject.startDate ?? dateTodayApiFormat,
    endDate: filterObject.endDate ?? dateTodayApiFormat,
    states: filterObject.states ?? "",
    deliveryMode: filterObject.deliveryMode ?? "",
    pendingOrderItems: filterObject.pendingOrderItems ?? "",
    isSupplierInvoicePresent: filterObject.isSupplierInvoicePresent,
    voidedStatus: filterObject.voidedStatus ?? "",
  });

  const [customerId, setCustomerId] = useState(filterObject?.customerId || "");
  const [showAllStates, setShowAllStates] = useState(false);
  const STATES_LIMIT = 5;
  const [supplierOrderStatusBackup, setSupplierOrderStatusBackup] = useState<
    string | null
  >(null);
  const isSupplierInvoiceFilterActive =
    localFilterObject.isSupplierInvoicePresent === false;
  const shouldDisableConflictingFilters = isSupplierInvoiceFilterActive;

  const selectedStates = useMemo(() => {
    return (
      filterObject.states?.split("&states=")?.slice(1).filter(Boolean) || []
    );
  }, [filterObject.states]);

  const sortedStatesMemo = useMemo(() => {
    return sortStatesBySelection({
      selectedStates,
      allStates: states || [],
    });
  }, [selectedStates, states]);

  const displayedStates = useMemo(() => {
    return showAllStates
      ? sortedStatesMemo
      : sortedStatesMemo.slice(0, STATES_LIMIT);
  }, [showAllStates, sortedStatesMemo]);

  const handleSupplierInvoiceToggle = useCallback(
    (isChecked: boolean) => {
      if (isChecked) {
        setSupplierOrderStatusBackup(localFilterObject.supplierOrderStatus);
        setLocalFilterObject((prev) => ({
          ...prev,
          isSupplierInvoicePresent: false,
          supplierOrderStatus: ["", OrderStatus.READY].join(
            "&supplierOrderStatus=",
          ),
        }));
        return;
      }

      setLocalFilterObject((prev) => ({
        ...prev,
        isSupplierInvoicePresent: undefined,
        supplierOrderStatus:
          supplierOrderStatusBackup ?? prev.supplierOrderStatus,
      }));
      setSupplierOrderStatusBackup(null);
    },
    [localFilterObject.supplierOrderStatus, supplierOrderStatusBackup],
  );

  const handleSingleStateCheckBoxClick = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleCheckboxFilterChange(e.target.value, "states", e.target.checked);
    },
    [handleCheckboxFilterChange],
  );

  useEffect(() => {
    setLocalFilterObject({
      ...localFilterObject,
      customerId: customerId,
    });
  }, [customerId]);

  function handleFilterApply() {
    setFilterObject({ ...localFilterObject, customerId: customerId });
    setQueryOffset(0);
    setOrderStartIndex(1);
    setIsFiltersChanged(true);
    onClose();
  }

  function handleClearFilters() {
    setCustomerId("");
    setSupplierOrderStatusBackup(null);
    setLocalFilterObject((prev) => ({
      ...prev,
      orderStatus: ["", ...orderStatuses].join("&orderStatus="),
      supplierOrderStatus: ["", ...supplierOrderStatuses].join(
        "&supplierOrderStatus=",
      ),
      customerType: "",
      orderHandler: [
        "",
        ...allOrderHandlers.map((item: { _id: string }) => item._id),
      ].join("&orderHandlerId="),
      filterOrderNo: "",
      filterOrderNoCheckBoxValue: false,
      customerId: "",
      customerName: "",
      orderType: ["", ...orderTypes].join("&orderType="),
      transactionType: ["", ...transactionTypes].join("&transactionType="),
      invoiceStatus: ["", ...invoiceStatus].join("&invoiceStatus="),
      isSupplierInvoicePresent: undefined,
      startDate: firstDateOfMonthApiFormat,
      endDate: dateTodayApiFormat,
      states: "",
      deliveryMode: ["", ...deliveryModes].join("&deliveryMode="),
      pendingOrderItems: "",
      voidedStatus: "",
    }));
  }

  //Todo: use this dry function to impliment the checkbox filter change
  function handleCheckboxFilterChange(
    newValue: string,
    filterKey: keyof typeof localFilterObject,
    isChecked: boolean,
  ) {
    setLocalFilterObject((prev) => {
      const currentValue = String(prev[filterKey] ?? "");
      const tempArray =
        currentValue?.split(`&${filterKey}=`)?.slice(1).filter(Boolean) || [];

      if (isChecked) {
        if (!tempArray.includes(newValue)) {
          tempArray.push(newValue);
        }
      } else {
        const valueIndex = tempArray.indexOf(newValue);
        if (valueIndex > -1) {
          tempArray.splice(valueIndex, 1);
        }
      }

      const tempString =
        tempArray.length === 0 ? "" : ["", ...tempArray].join(`&${filterKey}=`);

      return {
        ...prev,
        [filterKey]: tempString,
      };
    });
  }

  function handleSingleClickOnTransactionTypeCheckbox(
    e: React.ChangeEvent<HTMLInputElement>,
  ) {
    let tempArray = localFilterObject.transactionType
      ?.split("&transactionType=")
      ?.slice(1);
    e.target.checked
      ? tempArray?.push(e.target.value)
      : tempArray?.splice(tempArray?.indexOf(e.target.value), 1);
    tempArray?.unshift("");
    let tempString = tempArray?.join("&transactionType=");

    setLocalFilterObject({
      ...localFilterObject,
      transactionType: tempString,
    });
  }
  function handleSingleStatusCheckBoxClick(
    e: React.ChangeEvent<HTMLInputElement>,
  ) {
    let tempArray = localFilterObject.orderStatus
      .split("&orderStatus=")
      .slice(1);
    e.target.checked
      ? tempArray.push(e.target.value)
      : tempArray.splice(tempArray.indexOf(e.target.value), 1);
    tempArray.unshift("");
    let tempString = tempArray.join("&orderStatus=");
    setLocalFilterObject({
      ...localFilterObject,
      orderStatus: tempString,
    });
  }

  function handleSingleTypeCheckBoxClick(
    e: React.ChangeEvent<HTMLInputElement>,
  ) {
    let tempArray = localFilterObject.orderType?.split("&orderType=")?.slice(1);
    e.target.checked
      ? tempArray?.push(e.target.value)
      : tempArray?.splice(tempArray?.indexOf(e.target.value), 1);
    tempArray?.unshift("");
    let tempString = tempArray?.join("&orderType=");
    setLocalFilterObject({
      ...localFilterObject,
      orderType: tempString,
    });
  }

  function handleSingleSupplierOrderStatusCheckBoxClick(
    e: React.ChangeEvent<HTMLInputElement>,
  ) {
    let tempArray = localFilterObject.supplierOrderStatus
      .split("&supplierOrderStatus=")
      .slice(1);
    e.target.checked
      ? tempArray.push(e.target.value)
      : tempArray.splice(tempArray.indexOf(e.target.value), 1);
    tempArray.unshift("");
    let tempString = tempArray.join("&supplierOrderStatus=");
    setLocalFilterObject({
      ...localFilterObject,
      supplierOrderStatus: tempString,
    });
  }

  const handleSingleDeliveryModeCheckBoxClick = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleCheckboxFilterChange(
        e.target.value,
        "deliveryMode",
        e.target.checked,
      );
    },
    [handleCheckboxFilterChange],
  );

  const handleSinglePendingOrderItemsCheckBoxClick = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleCheckboxFilterChange(
        e.target.value,
        "pendingOrderItems",
        e.target.checked,
      );
    },
    [handleCheckboxFilterChange],
  );

  const handleSingleInvoiceStatusCheckBoxClick = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setLocalFilterObject((prev) => {
        const currentValue = prev.invoiceStatus ?? "";
        const tempArray =
          currentValue?.split("&invoiceStatus=")?.slice(1)?.filter(Boolean) ||
          [];
        if (e.target.checked) {
          tempArray.push(e.target.value);
        } else {
          tempArray.splice(tempArray.indexOf(e.target.value), 1);
        }
        const newInvoiceStatus =
          tempArray.length === 0
            ? ""
            : ["", ...tempArray].join("&invoiceStatus=");
        return {
          ...prev,
          invoiceStatus: newInvoiceStatus,
        };
      });
    },
    [],
  );

  const handleClearCustomer = () => {
    setLocalFilterObject({
      ...localFilterObject,
      customerId: "",
      customerName: "",
    });
    setCustomerId("");
  };

  const handleSearchChange = (value: string) => {
    setLocalFilterObject({
      ...localFilterObject,
      customerName: value,
    });
  };

  const onCancelClick = () => {
    setLocalFilterObject({
      ...filterObject,
      isSupplierInvoicePresent: filterObject.isSupplierInvoicePresent,
    });
    setSupplierOrderStatusBackup(null);
    onClose();
  };

  return (
    <Drawer
      isOpen={isOpen}
      placement="right"
      onClose={onClose}
      onOverlayClick={onCancelClick}
      returnFocusOnClose={false}
    >
      <DrawerContent
        onKeyDown={(e) => {
          if (e.key !== "Enter" || e.defaultPrevented) return;

          e.preventDefault();

          if (
            localFilterObject.filterOrderNoCheckBoxValue &&
            localFilterObject.filterOrderNo === ""
          ) {
            return;
          }

          handleFilterApply();
        }}
      >
        <DrawerHeader>
          <Box
            display={"flex"}
            justifyContent={"space-between"}
            borderBottom={"1px solid lightgrey"}
          >
            <Text height={"fit-content"} fontSize={"x-large"}>
              Filters
            </Text>
            <Text
              fontSize={"small"}
              display={"flex"}
              alignItems={"center"}
              mr={4}
              color={"#4895ef"}
              cursor={"pointer"}
              textDecoration={"underline"}
              onClick={handleClearFilters}
            >
              Reset Filters
            </Text>
          </Box>
        </DrawerHeader>

        <DrawerBody>
          <Box>
            <Box display={"flex"} mb={1} borderBottom={"0.5px solid lightgrey"}>
              <Checkbox
                isChecked={localFilterObject.filterOrderNoCheckBoxValue}
                onChange={(e) => {
                  setLocalFilterObject({
                    ...localFilterObject,
                    filterOrderNoCheckBoxValue: e.target.checked,
                  });
                }}
              ></Checkbox>
              <Text ml="3" fontWeight={"bold"} fontSize={"lg"}>
                Order/Enquiry Number
              </Text>
            </Box>
            <Text fontSize={"xx-small"}>Ends with</Text>

            <Input
              type="text"
              size={"sm"}
              value={localFilterObject.filterOrderNo}
              onChange={(e) => {
                setLocalFilterObject({
                  ...localFilterObject,
                  filterOrderNo: e.target.value,
                });
              }}
              onKeyDown={(e) => {
                if (e.key !== "Enter") return;
                e.preventDefault();
                if (
                  localFilterObject.filterOrderNoCheckBoxValue &&
                  localFilterObject.filterOrderNo === ""
                ) {
                  return;
                }
                handleFilterApply();
              }}
            ></Input>
            {localFilterObject.filterOrderNo === "" &&
              localFilterObject.filterOrderNoCheckBoxValue && (
                <Text color={"red"} fontSize={"xs"}>
                  Enter Order/Enquiry Number
                </Text>
              )}
          </Box>

          <Box
            display={"flex"}
            width={"100%"}
            flexDirection={"column"}
            height={"100%"}
            opacity={localFilterObject.filterOrderNoCheckBoxValue ? "0.5" : "1"}
          >
            <Box mt={5}>
              <Box
                display={"flex"}
                justifyContent={"space-between"}
                mb={3}
                borderBottom={"0.5px solid lightgrey"}
              >
                <Text fontWeight={"bold"} fontSize={"lg"}>
                  Order Type
                </Text>
                <Checkbox
                  isDisabled={localFilterObject.filterOrderNoCheckBoxValue}
                  onChange={(e) => {
                    e.target.checked
                      ? setLocalFilterObject({
                        ...localFilterObject,
                        orderType:
                          "&orderType=REGULAR&orderType=RETURN&orderType=SUBSCRIPTION",
                      })
                      : setLocalFilterObject({
                        ...localFilterObject,
                        orderType: "",
                      });
                  }}
                  isChecked={
                    localFilterObject.orderType?.split("&orderType=")?.slice(1)
                      .length === 3
                  }
                />
              </Box>
              <VStack alignItems={"start"}>
                <CheckboxGroup
                  value={localFilterObject.orderType
                    ?.split("&orderType=")
                    ?.slice(1)}
                  isDisabled={localFilterObject.filterOrderNoCheckBoxValue}
                >
                  {orderTypes.map((item, index) => {
                    return (
                      <GaraazCheckBoxTitle
                        key={index}
                        handleChange={handleSingleTypeCheckBoxClick}
                        title={item}
                        checkBoxValue={item.toUpperCase()}
                      ></GaraazCheckBoxTitle>
                    );
                  })}
                </CheckboxGroup>
              </VStack>
            </Box>
            <Box mt={5}>
              <Box
                display={"flex"}
                justifyContent={"space-between"}
                mb={3}
                borderBottom={"0.5px solid lightgrey"}
              >
                <Text fontWeight={"bold"} fontSize={"lg"}>
                  Transaction Type
                </Text>
                <Checkbox
                  isDisabled={localFilterObject.filterOrderNoCheckBoxValue}
                  onChange={(e) => {
                    e.target.checked
                      ? setLocalFilterObject({
                        ...localFilterObject,
                        transactionType:
                          "&transactionType=Trading&transactionType=Facilitation&transactionType=NONE",
                      })
                      : setLocalFilterObject({
                        ...localFilterObject,
                        transactionType: "",
                      });
                  }}
                  isChecked={
                    localFilterObject.transactionType
                      ?.split("&transactionType=")
                      ?.slice(1).length === 3
                  }
                />
              </Box>
              <VStack alignItems={"start"}>
                <CheckboxGroup
                  value={localFilterObject.transactionType
                    ?.split("&transactionType=")
                    ?.slice(1)}
                  isDisabled={localFilterObject.filterOrderNoCheckBoxValue}
                >
                  {transactionTypes.map((item, index) => {
                    return (
                      <GaraazCheckBoxTitle
                        key={index}
                        handleChange={
                          handleSingleClickOnTransactionTypeCheckbox
                        }
                        title={item}
                        checkBoxValue={item}
                      ></GaraazCheckBoxTitle>
                    );
                  })}
                </CheckboxGroup>
              </VStack>
            </Box>
            <Box mt="4">
              <Box
                display={"flex"}
                justifyContent={"space-between"}
                mb={3}
                borderBottom={"0.5px solid lightgrey"}
              >
                <Text fontWeight={"bold"} fontSize={"lg"}>
                  Garaaz Invoice Status
                </Text>
                <Checkbox
                  isDisabled={localFilterObject.filterOrderNoCheckBoxValue}
                  onChange={(e) => {
                    e.target.checked
                      ? setLocalFilterObject({
                        ...localFilterObject,
                        invoiceStatus:
                          "&invoiceStatus=PAID&invoiceStatus=UNPAID&invoiceStatus=RECEIVED",
                      })
                      : setLocalFilterObject({
                        ...localFilterObject,
                        invoiceStatus: "",
                      });
                  }}
                  isChecked={
                    (
                      localFilterObject.invoiceStatus
                        ?.split("&invoiceStatus=")
                        ?.slice(1)
                        ?.filter(Boolean) || []
                    ).length === invoiceStatus.length
                  }
                />
              </Box>
              <VStack alignItems={"start"}>
                <CheckboxGroup
                  value={
                    localFilterObject.invoiceStatus
                      ?.split("&invoiceStatus=")
                      ?.slice(1)
                      ?.filter(Boolean) || []
                  }
                  isDisabled={localFilterObject.filterOrderNoCheckBoxValue}
                >
                  <GaraazCheckBoxTitle
                    handleChange={handleSingleInvoiceStatusCheckBoxClick}
                    title="PAID"
                    checkBoxValue="PAID"
                    isUpperCase={false}
                  />
                  <GaraazCheckBoxTitle
                    handleChange={handleSingleInvoiceStatusCheckBoxClick}
                    title="RECEIVED"
                    checkBoxValue="RECEIVED"
                    isUpperCase={false}
                  />
                  <GaraazCheckBoxTitle
                    handleChange={handleSingleInvoiceStatusCheckBoxClick}
                    title="UNPAID"
                    checkBoxValue="UNPAID"
                    isUpperCase={false}
                  />
                </CheckboxGroup>
              </VStack>
            </Box>
            <Box mt="4">
              <Text
                fontWeight={"bold"}
                fontSize={"lg"}
                mb={3}
                borderBottom={"0.5px solid lightgrey"}
              >
                Supplier Invoice Status
              </Text>
              <Checkbox
                size="sm"
                isChecked={isSupplierInvoiceFilterActive}
                onChange={(e) => handleSupplierInvoiceToggle(e.target.checked)}
                isDisabled={localFilterObject.filterOrderNoCheckBoxValue}
              >
                Un-invoiced
              </Checkbox>
            </Box>
            <Box mt="4">
              <Text
                fontWeight={"bold"}
                fontSize={"lg"}
                mb={3}
                borderBottom={"0.5px solid lightgrey"}
              >
                Customer
              </Text>

              <RadioGroup
                onChange={(e) => {
                  setLocalFilterObject({
                    ...localFilterObject,
                    customerType:
                      e === CustomerType.SUBSCRIBED
                        ? CustomerType.SUBSCRIBED
                        : e === CustomerType.PLATFORM
                          ? CustomerType.PLATFORM
                          : e === "ANY"
                            ? ""
                            : "",
                    customerId: "",
                    customerName: "",
                  });
                  setCustomerId("");
                }}
                value={
                  localFilterObject.customerType === "customerSearch"
                    ? "customerSearch"
                    : localFilterObject.customerType === CustomerType.SUBSCRIBED
                      ? CustomerType.SUBSCRIBED
                      : localFilterObject?.customerType ===
                        CustomerType.PLATFORM
                        ? CustomerType.PLATFORM
                        : "Any"
                }
                isDisabled={localFilterObject.filterOrderNoCheckBoxValue}
              >
                <VStack alignItems={"start"}>
                  <Radio size={"sm"} value="Any">
                    Any
                  </Radio>
                  <Radio size={"sm"} value={CustomerType.PLATFORM}>
                    Platform
                  </Radio>
                  <Radio size={"sm"} value={CustomerType.SUBSCRIBED}>
                    Subscribed
                  </Radio>
                </VStack>
              </RadioGroup>
              <HStack mt="3">
                <Radio
                  size={"sm"}
                  isChecked={
                    localFilterObject.customerType === "customerSearch"
                  }
                  onChange={() => {
                    setLocalFilterObject({
                      ...localFilterObject,
                      customerType: "customerSearch",
                    });
                  }}
                />
                <GaraazSearch
                  placeholder={TextLabels.CUSTOMER_NAME_OR_NUMBER}
                  queryUrl={endpoints.searchXpressCustomer}
                  axios={authAxios}
                  onSelect={(customer: any) => {
                    setCustomerId(customer._id);
                  }}
                  onClear={handleClearCustomer}
                  onChange={handleSearchChange}
                  acceptMobileNumber={true}
                  hideSearchIcon={true}
                  idField="_id"
                  valueField="name"
                  value={localFilterObject.customerName}
                  isDisabled={
                    localFilterObject.filterOrderNoCheckBoxValue ||
                    localFilterObject.customerType !== "customerSearch"
                  }
                  searchListWidth="74%"
                />
              </HStack>
            </Box>

            <Box mt={5}>
              <Box
                display={"flex"}
                justifyContent={"space-between"}
                mb={3}
                borderBottom={"0.5px solid lightgrey"}
              >
                <Text fontWeight={"bold"} fontSize={"lg"}>
                  Delivery Mode
                </Text>
                <Checkbox
                  isDisabled={localFilterObject.filterOrderNoCheckBoxValue}
                  onChange={(e) => {
                    e.target.checked
                      ? setLocalFilterObject({
                        ...localFilterObject,
                        deliveryMode: ["", ...deliveryModes].join(
                          "&deliveryMode=",
                        ),
                      })
                      : setLocalFilterObject({
                        ...localFilterObject,
                        deliveryMode: "",
                      });
                  }}
                  isChecked={
                    localFilterObject.deliveryMode
                      ?.split("&deliveryMode=")
                      ?.slice(1)?.length === deliveryModes.length
                  }
                />
              </Box>
              <VStack align="start" spacing={2}>
                <CheckboxGroup
                  value={localFilterObject.deliveryMode
                    ?.split("&deliveryMode=")
                    ?.slice(1)}
                  isDisabled={localFilterObject.filterOrderNoCheckBoxValue}
                >
                  {deliveryModes.map((item, index) => (
                    <GaraazCheckBoxTitle
                      key={index}
                      handleChange={handleSingleDeliveryModeCheckBoxClick}
                      title={
                        item === "DOOR_DELIVERY"
                          ? "Door Delivery"
                          : "Self Pickup"
                      }
                      checkBoxValue={item}
                    />
                  ))}
                </CheckboxGroup>
              </VStack>
            </Box>

            <Box mt={5}>
              <Box
                display={"flex"}
                justifyContent={"space-between"}
                mb={3}
                borderBottom={"0.5px solid lightgrey"}
              >
                <Text fontWeight={"bold"} fontSize={"lg"}>
                  Customer Location
                </Text>
                <Checkbox
                  isDisabled={
                    localFilterObject.filterOrderNoCheckBoxValue ||
                    isLoadingStates
                  }
                  onChange={(e) => {
                    e.target.checked
                      ? setLocalFilterObject({
                        ...localFilterObject,
                        states: ["", ...(states || [])].join("&states="),
                      })
                      : setLocalFilterObject({
                        ...localFilterObject,
                        states: "",
                      });
                  }}
                  isChecked={
                    localFilterObject.states?.split("&states=")?.slice(1)
                      ?.length === states?.length
                  }
                />
              </Box>
              <VStack alignItems={"start"}>
                <CheckboxGroup
                  value={localFilterObject.states?.split("&states=")?.slice(1)}
                  isDisabled={localFilterObject.filterOrderNoCheckBoxValue}
                >
                  {displayedStates?.map((item: string, index: number) => {
                    return (
                      <GaraazCheckBoxTitle
                        key={`state-${item}-${index}`}
                        handleChange={handleSingleStateCheckBoxClick}
                        title={item}
                        checkBoxValue={item}
                        isUpperCase={false}
                      ></GaraazCheckBoxTitle>
                    );
                  })}
                </CheckboxGroup>
                {states && states.length > STATES_LIMIT && (
                  <Button
                    size="sm"
                    variant="ghost"
                    colorScheme="blue"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    w="100%"
                    _hover={{ bg: "blue.50" }}
                    onClick={() => setShowAllStates(!showAllStates)}
                    mt={0}
                  >
                    {showAllStates
                      ? "See Less"
                      : `See More (${states.length - STATES_LIMIT} more)`}
                  </Button>
                )}
              </VStack>
            </Box>
            <GaraazDateRange
              startDate={localFilterObject.startDate}
              endDate={localFilterObject.endDate}
              onDateChange={(startDate: any, endDate: any) => {
                setLocalFilterObject({
                  ...localFilterObject,
                  startDate: startDate,
                  endDate: endDate,
                });
              }}
              isDisabled={localFilterObject.filterOrderNoCheckBoxValue}
              minDate={localFilterObject.startDate}
              maxDate={dateTodayApiFormat}
            />
            <Box mt={5}>
              <Box
                display={"flex"}
                justifyContent={"space-between"}
                mb={3}
                borderBottom={"0.5px solid lightgrey"}
              >
                <Text fontWeight={"bold"} fontSize={"lg"}>
                  Status
                </Text>
                <Checkbox
                  isDisabled={localFilterObject.filterOrderNoCheckBoxValue}
                  onChange={(e) => {
                    e.target.checked
                      ? setLocalFilterObject({
                        ...localFilterObject,
                        orderStatus: ["", ...orderStatuses].join(
                          "&orderStatus=",
                        ),
                      })
                      : setLocalFilterObject({
                        ...localFilterObject,
                        orderStatus: "",
                      });
                  }}
                  isChecked={
                    localFilterObject.orderStatus
                      .split("&orderStatus=")
                      .slice(1).length === orderStatuses.length
                  }
                />
              </Box>
              <VStack alignItems={"start"}>
                <CheckboxGroup
                  value={localFilterObject.orderStatus
                    .split("&orderStatus=")
                    .slice(1)}
                  isDisabled={localFilterObject.filterOrderNoCheckBoxValue}
                >
                  {orderStatuses.map((item, index) => {
                    return (
                      <GaraazCheckBoxTitle
                        key={index}
                        handleChange={handleSingleStatusCheckBoxClick}
                        title={
                          item === "ACCEPTED"
                            ? "SUBSCRIPTION ACCEPTED"
                            : item === "REJECTED"
                              ? "SUBSCRIPTION REJECTED"
                              : item === "ENQUIRY_CLOSED"
                                ? "ENQUIRY CLOSED"
                                : item
                        }
                        checkBoxValue={item.toUpperCase()}
                      ></GaraazCheckBoxTitle>
                    );
                  })}
                </CheckboxGroup>
              </VStack>
            </Box>
            <Box mt={5}>
              <Box
                display={"flex"}
                justifyContent={"space-between"}
                mb={3}
                borderBottom={"0.5px solid lightgrey"}
              >
                <Text fontWeight={"bold"} fontSize={"lg"}>
                  Voided Orders
                </Text>
              </Box>
              <VStack alignItems={"start"}>
                <CheckboxGroup
                  value={localFilterObject.voidedStatus === "VOIDED" ? ["VOIDED"] : []}
                  isDisabled={localFilterObject.filterOrderNoCheckBoxValue}
                >
                  <GaraazCheckBoxTitle
                    handleChange={(e) => {
                      setLocalFilterObject({
                        ...localFilterObject,
                        voidedStatus: e.target.checked ? "VOIDED" : "",
                      });
                    }}
                    title="INCLUDE VOIDED"
                    checkBoxValue="VOIDED"
                    isUpperCase={false}
                  />
                </CheckboxGroup>
              </VStack>
            </Box>
            <Box mt={5}>
              <Box
                display={"flex"}
                justifyContent={"space-between"}
                mb={3}
                borderBottom={"0.5px solid lightgrey"}
              >
                <Text fontWeight={"bold"} fontSize={"lg"}>
                  Supplier Status
                </Text>
                <Checkbox
                  isDisabled={
                    localFilterObject.filterOrderNoCheckBoxValue ||
                    shouldDisableConflictingFilters
                  }
                  onChange={(e) => {
                    e.target.checked
                      ? setLocalFilterObject({
                        ...localFilterObject,
                        supplierOrderStatus: [
                          "",
                          ...supplierOrderStatuses,
                        ].join("&supplierOrderStatus="),
                      })
                      : setLocalFilterObject({
                        ...localFilterObject,
                        supplierOrderStatus: "",
                      });
                  }}
                  isChecked={
                    localFilterObject.supplierOrderStatus
                      ?.split("&supplierOrderStatus=")
                      .slice(1).length === supplierOrderStatuses.length
                  }
                />
              </Box>
              <VStack alignItems={"start"}>
                <CheckboxGroup
                  value={localFilterObject.supplierOrderStatus
                    ?.split("&supplierOrderStatus=")
                    .slice(1)}
                  isDisabled={
                    localFilterObject.filterOrderNoCheckBoxValue ||
                    shouldDisableConflictingFilters
                  }
                >
                  {supplierOrderStatuses.map((item, index) => {
                    return (
                      <GaraazCheckBoxTitle
                        key={index}
                        handleChange={
                          handleSingleSupplierOrderStatusCheckBoxClick
                        }
                        title={item === "NONE" ? "Not Allocated" : item}
                        checkBoxValue={item.toUpperCase()}
                      ></GaraazCheckBoxTitle>
                    );
                  })}
                </CheckboxGroup>
              </VStack>
            </Box>
            <Box mt={5}>
              <Box
                display={"flex"}
                justifyContent={"space-between"}
                mb={3}
                borderBottom={"0.5px solid lightgrey"}
              >
                <Text fontWeight={"bold"} fontSize={"lg"}>
                  Unprocessed Items
                </Text>
                <Checkbox
                  isDisabled={localFilterObject.filterOrderNoCheckBoxValue}
                  onChange={(e) => {
                    e.target.checked
                      ? setLocalFilterObject({
                        ...localFilterObject,
                        pendingOrderItems: ["", ...pendingOrderItems].join(
                          "&pendingOrderItems=",
                        ),
                      })
                      : setLocalFilterObject({
                        ...localFilterObject,
                        pendingOrderItems: "",
                      });
                  }}
                  isChecked={
                    localFilterObject.pendingOrderItems
                      ?.split("&pendingOrderItems=")
                      ?.slice(1)?.length === pendingOrderItems.length
                  }
                />
              </Box>
              <VStack align="start" spacing={2}>
                <CheckboxGroup
                  value={localFilterObject.pendingOrderItems
                    ?.split("&pendingOrderItems=")
                    ?.slice(1)}
                  isDisabled={localFilterObject.filterOrderNoCheckBoxValue}
                >
                  <GaraazCheckBoxTitle
                    handleChange={handleSinglePendingOrderItemsCheckBoxClick}
                    title="Active"
                    checkBoxValue="ACTIVE"
                    isUpperCase={true}
                  />
                  <GaraazCheckBoxTitle
                    handleChange={handleSinglePendingOrderItemsCheckBoxClick}
                    title="Supplier Partial Cancelled"
                    checkBoxValue="SUPPLIER_CANCELLED"
                    isUpperCase={true}
                  />
                  <GaraazCheckBoxTitle
                    handleChange={handleSinglePendingOrderItemsCheckBoxClick}
                    title="CBO"
                    checkBoxValue="CBO"
                    isUpperCase={true}
                  />
                </CheckboxGroup>
              </VStack>
            </Box>
            <Box mt={5}>
              <Box
                display={"flex"}
                justifyContent={"space-between"}
                mb={3}
                borderBottom={"0.5px solid lightgrey"}
              >
                <Text fontWeight={"bold"} fontSize={"lg"}>
                  Order Handler
                </Text>
                <Checkbox
                  onChange={(e) => {
                    e.target.checked
                      ? setLocalFilterObject({
                        ...localFilterObject,
                        orderHandler: [
                          "",
                          ...allOrderHandlers.map(
                            (item: { _id: string }) => item._id,
                          ),
                        ].join("&orderHandlerId="),
                      })
                      : setLocalFilterObject({
                        ...localFilterObject,
                        orderHandler: "",
                      });
                  }}
                  isChecked={
                    localFilterObject.orderHandler
                      ?.split("&orderHandlerId=")
                      ?.slice(1)?.length === allOrderHandlers?.length
                  }
                />
              </Box>
              <VStack alignItems={"start"}>
                <CheckboxGroup
                  value={localFilterObject?.orderHandler
                    ?.split("&orderHandlerId=")
                    ?.slice(1)}
                  onChange={(values) => {
                    setLocalFilterObject({
                      ...localFilterObject,
                      orderHandler: ["", ...values].join("&orderHandlerId="),
                    });
                  }}
                >
                  {allOrderHandlers?.map(
                    (
                      item: {
                        _id: string;
                        firstName: string;
                        lastName: string;
                      },
                      index: number,
                    ) => {
                      return (
                        <GaraazCheckBoxTitle
                          key={index}
                          handleChange={(e) => {
                            const isChecked = e.target.checked;
                            const id = item._id;
                            const existingHandlers =
                              localFilterObject.orderHandler
                                ?.split("&orderHandlerId=")
                                ?.slice(1) || [];
                            const updatedHandlers = isChecked
                              ? [...existingHandlers, id]
                              : existingHandlers.filter(
                                (handlerId) => handlerId !== id,
                              );

                            setLocalFilterObject({
                              ...localFilterObject,
                              orderHandler: ["", ...updatedHandlers].join(
                                "&orderHandlerId=",
                              ),
                            });
                          }}
                          title={`${item.firstName} ${item.lastName}`}
                          checkBoxValue={item._id}
                          isUpperCase={false}
                        ></GaraazCheckBoxTitle>
                      );
                    },
                  )}
                </CheckboxGroup>
              </VStack>
            </Box>
          </Box>
        </DrawerBody>

        <DrawerFooter>
          <Button variant="outline" onClick={onCancelClick} mr={3}>
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={handleFilterApply}
            backgroundColor={"rgba(72,149,239,0.8)"}
            color={"white"}
            isDisabled={
              localFilterObject.filterOrderNoCheckBoxValue &&
              localFilterObject.filterOrderNo === ""
            }
            _hover={{ backgroundColor: "rgba(72,149,239,1)" }}
            rightIcon={<MdKeyboardReturn />}
          >
            Apply
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default SideFilterTab;
